import React from 'react';
import {
  View,
  Pressable,
  Text,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import moment from 'moment';
import Localized from 'src/constants/AppStrings';
import Styles from '../../../Styles';
import {ScreenProps} from 'src/types/Screen';
import {DealStatus} from 'src/services/aws/DealRepository';
import DealHelper from '../../../helpers/DealHelper';
import Settings from 'src/Settings';
import {getDescriber} from '../../descriptor/DescriptorType';
import ChevronRight from 'src/components/img/svg/ChevronRight';
import {GiftItem} from 'src/types/Snack';
import AVText from 'src/components/elements/AVText';
import UnReadlIcon from 'src/components/img/svg/UnReadIcon';
import {getNotificationDescriber} from 'src/components/elements/notificationDescriptor/DescriptorType';
import {MessageTypeEnum} from 'src/constants/MessageTypeEnum';

type DealProps = ScreenProps & {
  dealItem: GiftItem;
  selectedItemIndex: number;
  onPress: (giftItem: GiftItem, selectedItemIndex: number) => void;
};
type DealState = {
  expired: boolean;
  completed: boolean;
};

const margin = Styles.Spacing.m3 + Styles.Spacing.m2;

class DealItem extends React.Component<DealProps, DealState> {
  acceptingDeal = false;

  constructor(props: DealProps) {
    super(props);
    this.state = {
      expired: moment().isAfter(moment(props.dealItem.endDate)),
      completed: props.dealItem.dealStatus === DealStatus.redeemed,
    };
    this.renderClaimSection = this.renderClaimSection.bind(this);
    this.renderExpirationText = this.renderExpirationText.bind(this);
    this.acceptDeal = this.acceptDeal.bind(this);
    this.onPress = this.onPress.bind(this);
  }

  async acceptDeal() {
    if (!Settings.isRefiveAnd365Pay()) {
      const {...rest} = this.props;
      DealHelper.claimDeal(
        rest,
        this.props.dealItem.externalServiceId as string,
      );
    }
  }

  onPress() {
    if (this.props.onPress) {
      this.props.onPress(this.props.dealItem, 0);
    }
  }

  renderClaimSection() {
    if (this.props.dealItem.dealStatus === DealStatus.pulled) {
      return (
        <View
          style={[
            styles.claimDealBtn,
            {
              top: 10,
            },
          ]}
        >
          <Pressable
            style={[
              styles.claimButton,
              getNotificationDescriber().getDealClaimButtonViewStyle(),
            ]}
            onPress={this.acceptDeal}
            role="button"
            accessibilityLabel={Localized.Labels.claim}
            nativeID={Localized.Labels.claim}
            aria-label={Localized.Labels.claim}
            accessibilityRole="button"
          >
            <AVText
              style={[
                styles.claimBtnTxt,
                getNotificationDescriber().getDealClaimButtonTextStyle(),
              ]}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            >
              {Localized.Labels.claim}
            </AVText>
          </Pressable>
        </View>
      );
    } else if (this.props.dealItem.dealStatus === DealStatus.redeemed) {
      let text: string = Localized.Labels.redeemed_1_time;

      if (this.props.dealItem.redeemedCount > 1) {
        text = Localized.Labels.formatString(
          Localized.Labels.redeemed_x_times,
          this.props.dealItem.redeemedCount,
        ) as string;
      }

      return (
        <Text
          nativeID={`Deal_${text}`}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm10}
          style={[styles.dealTarget, styles.successText]}
        >
          {text}
        </Text>
      );
    }

    return null;
  }

  renderExpirationText() {
    const expirationProps = {
      styles,
      deal: this.props.dealItem,
      expired: this.state.expired,
    };
    return getDescriber().dealRenderExpirationTxtTemplate(expirationProps);
  }
  getCPDateStyle = (): StyleProp<ViewStyle> => {
    if (this.state.completed || this.state.expired) {
      return {left: 0, paddingRight: Styles.Spacing.m3, marginTop: -4};
    } else if (!this.props.dealItem.isRead) {
      return {left: 45, marginTop: -4};
    } else if (this.props.dealItem.isRead) {
      return {left: 22, marginTop: -4};
    }
  };
  renderArrow() {
    if (
      !Settings.isRefiveAnd365Pay() &&
      (this.state.expired || this.state.completed)
    ) {
      return null;
    }
    return (
      <>
        {Settings.isRefiveAnd365Pay() && (
          <View
            style={getDescriber().getSnackItemStyleDescriptor().chevronRight}
            accessible
            accessibilityLabel="Snack Item Icon"
          >
            <ChevronRight />
          </View>
        )}
      </>
    );
  }

  render() {
    const describerProps = {
      styles,
      deal: this.props.dealItem,
      expired: this.state.expired,
    };

    return (
      <Pressable
        disabled={this.state.expired || this.state.completed}
        style={[
          getDescriber().dealStyleDescriptor().container,
          !Settings.isCanteen() && Styles.Style.shadow,
          !Settings.isRefiveAnd365Pay() && {
            borderBottomWidth: 1,
            borderBottomColor: '#9A9A9F',
            alignSelf: 'center',
            width: '90%',
          },
        ]}
        onPress={this.onPress}
        nativeID={'Btn_' + MessageTypeEnum.offers}
      >
        {getDescriber().dealRenderDealIcon(describerProps)}
        <View style={styles.mainContent}>
          {!Settings.isRefiveAnd365Pay() && (
            <View style={styles.titleHeaderView}>
              <Text
                style={getDescriber().dealStyleDescriptor().titleHeader}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm10}
                accessibilityLabel={Localized.Labels.offers}
                accessible
                testID="qa_NotificationType_txt"
                nativeID="qa_NotificationType_txt"
              >
                {Localized.Labels.offers}
              </Text>
              <View style={[styles.dateRow]}>
                {this.renderExpirationText()}
              </View>
            </View>
          )}
          <View
            style={[
              styles.topSection,
              !Settings.isRefiveAnd365Pay() && {alignItems: 'center', gap: 8},
            ]}
          >
            {!Settings.isRefiveAnd365Pay() && !this.props.dealItem.isRead && (
              <UnReadlIcon
                color={getNotificationDescriber().getUnreadMessageIconColor()}
              />
            )}
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm10}
              accessibilityLabel={getDescriber().dealRenderDealTopSection(
                describerProps,
              )}
              style={getDescriber().dealStyleDescriptor().dealName}
              numberOfLines={getDescriber().dealItemNumberDescriptor()}
              accessible
              testID="qa_MessageSubject_txt"
              nativeID="qa_MessageSubject_txt"
            >
              {getDescriber().dealRenderDealTopSection(describerProps)}
            </AVText>
            {Settings.isRefiveAnd365Pay() && this.renderExpirationText()}
          </View>

          {getDescriber().dealRenderDealNameDesc(describerProps)}
          {!Settings.isRefiveAnd365Pay() && this.renderClaimSection()}
        </View>
        {this.renderArrow()}
      </Pressable>
    );
  }
}

const styles = StyleSheet.create({
  arrow: {
    width: 24,
    height: 24,
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
  },
  container: {
    borderRadius: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m2,
    paddingHorizontal: Styles.Spacing.m3,
    flexDirection: 'row',
    paddingVertical: Styles.Spacing.m3,
    flex: 1,
    marginHorizontal: margin,
  },
  icon: {
    borderRadius: 32,
    width: 50,
    height: 50,
    backgroundColor: Styles.primaryColor,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topSection: {
    flexDirection: 'row',
  },
  mainContent: {
    flex: 1,
  },
  rightSection: {
    justifyContent: 'space-between',
  },
  message: {
    color: Styles.black,
    flex: 1,
    fontSize: Styles.Fonts.f8,
    marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
  },
  claimButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 32,
    borderWidth: 1,
    overflow: 'hidden',
  },
  dealTarget: {
    fontSize: Styles.Fonts.f1,
    color: Styles.lightGray,
    alignSelf: 'flex-start',
    marginTop: Styles.Spacing.m2,
    marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
  },
  successText: {
    color: Styles.blueRevolve,
    fontFamily: Styles.FontFamily.figtreeRegular,
  },
  claimBtnTxt: {
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: Settings.isRefive()
      ? Styles.FontFamily.figtreeRegular
      : Styles.FontFamily.robotoRegular,
  },
  claimDealBtn: {
    alignSelf: 'flex-end',
  },
  commonRow: {
    flexDirection: 'row',
  },
  dateRow: {
    marginBottom: 2,
    alignSelf: 'flex-end',
    justifyContent: 'flex-end',
    marginRight: 22,
  },
  titleHeaderView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 5,
  },
});
export default DealItem;
